import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { CircleAlert } from 'lucide-react';
import { contactsApi, customersApi } from '@/lib/api';
import {
  isValidEmail,
  getEmailErrorMessage,
  getPhoneErrorMessage,
  normalizePhoneNumber,
  filterPhoneInput,
  hasContactMethod,
  formatApiError,
} from '@/lib/validation';
import { herkunftAus, herkunftRueckweg } from '@/lib/herkunft';
import { Button, Callout, Card, Skeleton } from '@elbklar/ui';
import { CrmPageHeader } from '@/components/layout/CrmPageHeader';
import { TextareaField, TextField } from '@/components/form/Felder';
import type { ContactCreate } from '@/types';

interface FormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  mobile: string;
  role: string;
  notes: string;
  is_primary: boolean;
}

export function ContactFormPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  // Woher der Nutzer kommt (siehe lib/herkunft.ts): die Kundenseite, die
  // Partnerseite oder die allgemeine Kontaktliste. Beim Anlegen stand das
  // schon in der Adresse, beim Bearbeiten fehlte es.
  const herkunft = herkunftAus(searchParams);
  const { customerId, partnerId } = herkunft;

  // Ein Rueckweg fuer alles: Speichern, „Zurueck" und „Abbrechen". Ohne
  // Herkunft bleibt es wie bisher — beim Bearbeiten zur Kontaktseite, beim
  // Anlegen zur Kontaktliste bzw. zum neuen Kontakt.
  const rueckweg = herkunftRueckweg(herkunft);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { data: contact, isLoading: isLoadingContact } = useQuery({
    queryKey: ['contacts', id],
    queryFn: () => contactsApi.get(id!),
    enabled: isEdit,
  });

  const { data: customer } = useQuery({
    queryKey: ['customers', customerId],
    queryFn: () => customersApi.get(customerId!),
    enabled: !!customerId && !isEdit,
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      mobile: '',
      role: '',
      notes: '',
      is_primary: false,
    },
    values: contact
      ? {
          first_name: contact.first_name,
          last_name: contact.last_name,
          email: contact.email || '',
          phone: contact.phone || '',
          mobile: contact.mobile || '',
          role: contact.role || '',
          notes: contact.notes || '',
          is_primary: contact.is_primary,
        }
      : undefined,
  });

  // Track unsaved changes
  useEffect(() => {
    setHasUnsavedChanges(isDirty);
  }, [isDirty]);

  // Warn before leaving with unsaved changes (browser back/refresh)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const email = watch('email');
  const phone = watch('phone');
  const mobile = watch('mobile');
  const hasContact = hasContactMethod(email, phone, mobile);

  // Validation errors
  const emailError = getEmailErrorMessage(email);
  const phoneError = getPhoneErrorMessage(phone);
  const mobileError = getPhoneErrorMessage(mobile);

  const createMutation = useMutation({
    mutationFn: (data: ContactCreate) => contactsApi.create(data),
    onSuccess: (data) => {
      setHasUnsavedChanges(false);
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      navigate(rueckweg?.to ?? `/contacts/${data.id}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<ContactCreate>) => contactsApi.update(id!, data),
    onSuccess: () => {
      setHasUnsavedChanges(false);
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      navigate(rueckweg?.to ?? `/contacts/${id}`);
    },
  });

  const onSubmit = (data: FormData) => {
    // Normalize phone numbers before submitting
    const normalizedData = {
      ...data,
      phone: normalizePhoneNumber(data.phone),
      mobile: normalizePhoneNumber(data.mobile),
    };

    if (isEdit) {
      updateMutation.mutate(normalizedData);
    } else {
      createMutation.mutate({
        ...normalizedData,
        customer_id: customerId || undefined,
        partner_id: partnerId || undefined,
      });
    }
  };

  // Dasselbe Ziel wie nach dem Speichern: Ein Rueckweg, der nur beim
  // Speichern stimmt und beim Abbrechen nicht, ist halb behoben.
  const getBackUrl = () => rueckweg?.to ?? (isEdit ? `/contacts/${id}` : '/contacts');

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const mutationError = createMutation.error || updateMutation.error;

  // Check if form is valid for submission
  const isFormValid =
    hasContact && !emailError && !phoneError && !mobileError;

  if (isEdit && isLoadingContact) {
    return (
      <div className="space-y-6 pt-4">
        <div className="flex items-center gap-4">
          <Skeleton className="size-10 rounded-lg" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
    );
  }

  return (
    <div>
      <CrmPageHeader
        size="page"
        zurueck={{ to: getBackUrl(), label: 'Zurück' }}
        title={isEdit ? 'Kontakt bearbeiten' : 'Kontakt anlegen'}
        subtitle={!isEdit && customer ? `Zu ${customer.company_name}` : undefined}
      />

      {/* API Error */}
      {mutationError && (
        <Callout tone="danger" icon={CircleAlert} title="Speichern fehlgeschlagen" boxed className="mb-6">
          <span className="whitespace-pre-line">{formatApiError(mutationError)}</span>
        </Callout>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-6">
        {/* Personal Information */}
        <Card title="Persönliche Daten" description="Name und Position">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Controller
                name="first_name"
                control={control}
                rules={{ required: 'Vorname fehlt' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Vorname"
                    required
                    error={errors.first_name?.message}
                    placeholder="Max"
                  />
                )}
              />
              <Controller
                name="last_name"
                control={control}
                rules={{ required: 'Nachname fehlt' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nachname"
                    required
                    error={errors.last_name?.message}
                    placeholder="Mustermann"
                  />
                )}
              />
              <Controller
                name="role"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Position"
                    placeholder="Geschäftsführer"
                  />
                )}
              />
            </div>
        </Card>

        {/* Contact Information */}
        <Card title="Kontaktdaten" description="Alle Angaben optional">
            {/* Contact methods are optional */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Controller
                name="email"
                control={control}
                rules={{
                  validate: (value) => {
                    if (value && !isValidEmail(value)) {
                      return getEmailErrorMessage(value);
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="E-Mail"
                    type="email"
                    placeholder="max@firma.de"
                    error={emailError}
                  />
                )}
              />
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Telefon (Festnetz)"
                    placeholder="0351 1234567"
                    error={phoneError}
                    hint={
                      !phoneError
                        ? 'Wird beim Verlassen des Felds formatiert'
                        : undefined
                    }
                    onChange={(e) => {
                      field.onChange(filterPhoneInput(e.target.value));
                    }}
                    onBlur={() => {
                      if (field.value.trim()) {
                        setValue('phone', normalizePhoneNumber(field.value));
                      }
                    }}
                  />
                )}
              />
              <Controller
                name="mobile"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Mobil"
                    placeholder="0170 1234567"
                    error={mobileError}
                    hint={
                      !mobileError
                        ? 'Wird beim Verlassen des Felds formatiert'
                        : undefined
                    }
                    onChange={(e) => {
                      field.onChange(filterPhoneInput(e.target.value));
                    }}
                    onBlur={() => {
                      if (field.value.trim()) {
                        setValue('mobile', normalizePhoneNumber(field.value));
                      }
                    }}
                  />
                )}
              />
            </div>
        </Card>

        {/* Notes */}
        <Card title="Notizen" description="Interne Anmerkungen zum Kontakt">
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextareaField
                  {...field}
                  rows={4}
                  placeholder="Interne Notizen zum Kontakt"
                />
              )}
            />
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="ghost" onClick={() => navigate(getBackUrl())} disabled={isSubmitting}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" loading={isSubmitting} disabled={!isFormValid}>
            {isEdit ? 'Änderungen speichern' : 'Kontakt anlegen'}
          </Button>
        </div>
      </form>
    </div>
  );
}
